import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft } from 'lucide-react';

/**
 * Shared shell for public legal pages (Privacy, Terms).
 * No auth required. Matches AliVan's dark cyberpunk aesthetic.
 */
const LegalLayout = ({ title, subtitle, lastUpdated, children, testId }) => {
  return (
    <div
      className="min-h-screen bg-[#0A0A0A] text-white relative overflow-hidden"
      data-testid={testId}
    >
      {/* Ambient neon-red glow */}
      <div className="pointer-events-none absolute inset-0 opacity-40">
        <div className="absolute top-[-20%] left-[-10%] w-[60vw] h-[60vw] rounded-full bg-[radial-gradient(circle,rgba(255,0,51,0.10),transparent_60%)]" />
        <div className="absolute bottom-[-30%] right-[-10%] w-[50vw] h-[50vw] rounded-full bg-[radial-gradient(circle,rgba(255,0,51,0.06),transparent_60%)]" />
      </div>

      {/* Grid overlay */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
        }}
      />

      <div className="relative z-10 max-w-3xl mx-auto px-6 py-14 sm:py-20">
        <Link
          to="/"
          data-testid="legal-back-home"
          className="inline-flex items-center gap-1.5 font-jetbrains text-[11px] uppercase tracking-widest text-white/50 hover:text-neon-red transition-colors"
        >
          <ChevronLeft className="w-3 h-3" />
          Volver a AliVan
        </Link>

        <motion.header
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="mt-10 pb-8 border-b border-neon-red/20"
        >
          <p className="font-jetbrains text-[10px] uppercase tracking-[0.4em] text-neon-red">
            AliVan · Documento legal
          </p>
          <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold mt-3 text-white leading-tight">
            {title}
          </h1>
          {subtitle && (
            <p className="font-jetbrains text-sm text-white/60 mt-4 max-w-2xl leading-relaxed">
              {subtitle}
            </p>
          )}
          {lastUpdated && (
            <p className="font-jetbrains text-[11px] uppercase tracking-widest text-white/40 mt-4">
              Última actualización · {lastUpdated}
            </p>
          )}
        </motion.header>

        <motion.article
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15, ease: 'easeOut' }}
          className="prose prose-invert max-w-none mt-10 space-y-8"
        >
          {children}
        </motion.article>

        <div className="mt-16 pt-6 border-t border-neon-red/15 flex flex-wrap items-center justify-between gap-4">
          <p className="font-jetbrains text-[10px] uppercase tracking-widest text-white/40">
            © {new Date().getFullYear()} AliVan
          </p>
          <div className="flex gap-6">
            <Link
              to="/privacy"
              data-testid="footer-privacy"
              className="font-jetbrains text-[11px] uppercase tracking-widest text-white/50 hover:text-neon-red transition-colors"
            >
              Privacidad
            </Link>
            <Link
              to="/terms"
              data-testid="footer-terms"
              className="font-jetbrains text-[11px] uppercase tracking-widest text-white/50 hover:text-neon-red transition-colors"
            >
              Términos
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

// Small helper for consistent section blocks.
export const Section = ({ id, title, children }) => (
  <section
    id={id}
    data-testid={`legal-section-${id}`}
    className="border border-neon-red/15 bg-[#0F0F0F]/60 backdrop-blur-sm p-6 sm:p-8"
  >
    <h2 className="font-outfit text-xl sm:text-2xl font-bold text-white flex items-center gap-3">
      <span className="text-neon-red font-jetbrains text-sm">▶</span>
      {title}
    </h2>
    <div className="mt-4 space-y-3 font-jetbrains text-sm leading-relaxed text-white/75">
      {children}
    </div>
  </section>
);

export default LegalLayout;
